import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';

export default function ProfilePage() {
  const { t } = useTranslation(['profile', 'common']);
  const { user, tenant } = useAuth();

  const userInitials = useMemo(() => {
    if (!user) return 'U';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    if (first || last) return `${first}${last}`.toUpperCase();
    return user.email?.[0]?.toUpperCase() || 'U';
  }, [user]);

  const userRole = useMemo(() => {
    if (!user) return 'User';
    if (user.isSuperuser) return 'Administrator';
    if (user.isStaff) return 'Staff';
    return 'Dentist';
  }, [user]);

  const displayName = useMemo(() => {
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user?.email?.split('@')[0] || 'User';
  }, [user]);

  return (
    <>
      <Helmet>
        <title>{t('title')} - Doctif</title>
      </Helmet>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* ─── Header Card ─── */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-border p-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Avatar */}
            <div className="h-20 w-20 rounded-2xl bg-primary flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-white text-2xl font-bold tracking-wide">{userInitials}</span>
            </div>

            {/* Name & Meta */}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-foreground tracking-tight">{displayName}</h1>
              <p className="text-muted-foreground mt-0.5">{user?.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                  <ShieldCheckIcon className="h-3.5 w-3.5" />
                  {userRole}
                </span>
                {tenant && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground">
                    <BuildingOfficeIcon className="h-3.5 w-3.5" />
                    {tenant.name}
                  </span>
                )}
              </div>
            </div>

            {/* Edit button */}
            <Link
              to="/settings/account"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-card border border-border text-foreground font-medium text-sm hover:bg-muted hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200 shadow-sm flex-shrink-0"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              {t('editProfile', { defaultValue: 'Edit Profile' })}
            </Link>
          </div>
        </div>

        {/* ─── Info Grid ─── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <section className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <UserCircleIcon className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-base font-semibold text-foreground">{t('personalInfo.title')}</h2>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <InfoRow label={t('personalInfo.firstName')} value={user?.firstName || '—'} />
              <InfoRow label={t('personalInfo.lastName')} value={user?.lastName || '—'} />
              <InfoRow label={t('personalInfo.role')} value={userRole} />
            </div>
          </section>

          {/* Contact */}
          <section className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-sky-500/10 flex items-center justify-center">
                  <EnvelopeIcon className="h-5 w-5 text-sky-500" />
                </div>
                <h2 className="text-base font-semibold text-foreground">{t('contact', { defaultValue: 'Contact' })}</h2>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <InfoRow label={t('personalInfo.email')} value={user?.email || '—'} mono />
              <InfoRow label={t('personalInfo.phone')} value="—" />
            </div>
          </section>

          {/* Practice Information */}
          <section className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-emerald-500" />
                </div>
                <h2 className="text-base font-semibold text-foreground">{t('practice.title')}</h2>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <InfoRow label={t('practice.practiceName')} value={tenant?.name || '—'} />
              <InfoRow label={t('practice.subdomain')} value={tenant?.subdomain || '—'} mono />
              <InfoRow label={t('practice.practiceId')} value={tenant?.id || '—'} mono smaller />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('practice.plan')}</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-primary/10 text-primary border border-primary/20">
                  PRO
                </span>
              </div>
            </div>
          </section>

          {/* Security Overview */}
          <section className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <KeyIcon className="h-5 w-5 text-amber-500" />
                </div>
                <h2 className="text-base font-semibold text-foreground">{t('security.title')}</h2>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <InfoRow label={t('security.passwordLabel', { defaultValue: 'Password' })} value="••••••••" />
              <InfoRow label={t('security.twoFactor', { defaultValue: 'Two-Factor Auth' })} value={t('security.disabled', { defaultValue: 'Disabled' })} />
              <div className="pt-1">
                <Link
                  to="/settings/account"
                  className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {t('security.changePassword')} →
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

/* ─── Read-only info row ─── */
function InfoRow({
  label,
  value,
  mono,
  smaller,
}: {
  label: string;
  value: string;
  mono?: boolean;
  smaller?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-muted-foreground flex-shrink-0">{label}</span>
      <span
        className={`text-sm font-medium text-foreground text-right truncate ${mono ? 'font-mono' : ''} ${smaller ? 'text-xs' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}
